import { getStorage, ref, getDownloadURL, listAll } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

export function isDocxFileNameOrType(fileName?: string | null, contentType?: string | null): boolean {
  if (fileName?.toLowerCase().endsWith('.docx')) return true;
  const ct = (contentType || '').toLowerCase();
  return ct.includes('wordprocessingml') || ct.includes('officedocument.wordprocessingml.document');
}

export async function loadDocumentImages(
  documentId: string,
  fileName?: string,
  invoiceId?: string,
  userId?: string,
  documentCreatedAt?: Date,
  projectId?: string,
  storagePath?: string,
  fileUrl?: string,
  knowledgeId?: string,
  contentType?: string | null
) {
  const storage = getStorage();
  const urls: string[] = [];

  // Word documents: no in-browser preview; UI shows notice + download instead.
  if (knowledgeId && isDocxFileNameOrType(fileName, contentType)) {
    return urls;
  }

  if (knowledgeId) {
    if (storagePath) {
      try {
        const storageRef = ref(storage, storagePath);
        const url = await getDownloadURL(storageRef);
        urls.push(url);
        return urls;
      } catch {
        // Continue to fileUrl
      }
    }
    if (fileUrl) {
      urls.push(fileUrl);
      return urls;
    }
  }
  
  if (projectId && isDocxFileNameOrType(fileName, contentType)) {
    return urls;
  }

  if (projectId) {
    if (storagePath) {
      try {
        const storageRef = ref(storage, storagePath);
        const url = await getDownloadURL(storageRef);
        urls.push(url);
        return urls;
      } catch {
        // Continue to fileUrl fallback
      }
    }

    if (fileUrl) {
      urls.push(fileUrl);
      return urls;
    }

    return urls;
  }
  
  // First, try the new path structure: users/{userId}/invoices/{invoiceId}/page_X.jpg
  // This is for documents stored as multiple image files
  const tryLoadPageImages = async (folderId: string): Promise<string[]> => {
    const pageUrls: string[] = [];
    if (!userId || !folderId) return pageUrls;
    
    try {
      const invoiceFolderRef = ref(storage, `users/${userId}/invoices/${folderId}`);
      const listResult = await listAll(invoiceFolderRef);
      
      // Filter and sort page images (page_0.jpg, page_1.jpg, etc.)
      const pageFiles = listResult.items
        .filter(item => {
          const name = item.name.toLowerCase();
          return name.match(/^page_\d+\.(jpg|jpeg|png|webp)$/);
        })
        .sort((a, b) => {
          // Extract page numbers for sorting
          const pageNumA = parseInt(a.name.match(/page_(\d+)/)?.[1] || '0', 10);
          const pageNumB = parseInt(b.name.match(/page_(\d+)/)?.[1] || '0', 10);
          return pageNumA - pageNumB;
        });
      
      if (pageFiles.length > 0) {
        // Load all page images in order
        for (const pageFile of pageFiles) {
          try {
            const url = await getDownloadURL(pageFile);
            pageUrls.push(url);
          } catch (error: any) {
            // Silently continue if a file fails to load
            continue;
          }
        }
      }
    } catch (error: any) {
      // Silently continue if listAll fails
    }
    
    return pageUrls;
  };
  
  // Try with invoiceId first, then documentId as fallback
  // Note: invoiceId is typically document.id, documentId is typically document.documentId
  const folderIdsToTry = [];
  if (invoiceId) folderIdsToTry.push(invoiceId);
  if (documentId && documentId !== invoiceId) folderIdsToTry.push(documentId);
  
  for (const folderId of folderIdsToTry) {
    const pageUrls = await tryLoadPageImages(folderId);
    if (pageUrls.length > 0) {
      return pageUrls;
    }
  }
  
  // Fallback: Files are stored directly in invoices/{userId}/filename.pdf
  // Try to list files in invoices/{userId}/ and match them
  if (userId) {
    try {
      const userInvoicesRef = ref(storage, `invoices/${userId}`);
      const listResult = await listAll(userInvoicesRef);
      
      // Try to match files by documentId, invoiceId, or fileName
      const basePaths: string[] = [];
      if (documentId) basePaths.push(documentId);
      if (invoiceId && invoiceId !== documentId) basePaths.push(invoiceId);
      
      type MatchedFile = { ref: any; name: string; timeDiff?: number };
      let matchedFile: MatchedFile | null = null;
      
      for (const itemRef of listResult.items) {
        const itemName = itemRef.name;
        
        // Check if filename contains documentId or invoiceId
        if (basePaths.some(basePath => itemName.includes(basePath))) {
          matchedFile = { ref: itemRef, name: itemName };
          break; // Found exact match, stop
        }
        // Check if filename matches provided fileName
        else if (fileName && itemName.includes(fileName)) {
          matchedFile = { ref: itemRef, name: itemName };
          break; // Found exact match, stop
        }
        
        // If no exact match yet, try to extract timestamp from filename for date matching
        // Filenames are typically: {timestamp}_{originalName}
        if (documentCreatedAt) {
          const timestampMatch = itemName.match(/^(\d+)_/);
          if (timestampMatch) {
            const fileTimestamp = parseInt(timestampMatch[1], 10);
            const fileDate = new Date(fileTimestamp);
            const timeDiff = Math.abs(fileDate.getTime() - documentCreatedAt.getTime());
            // If file was created within 5 minutes of document creation, consider it a match
            if (timeDiff < 5 * 60 * 1000) {
              // Keep the file with the smallest time difference
              const existingTimeDiff = matchedFile?.timeDiff ?? Infinity;
              if (!matchedFile || timeDiff < existingTimeDiff) {
                matchedFile = { ref: itemRef, name: itemName, timeDiff };
              }
            }
          }
        }
      }
      
      // Try to load the matched file
      if (matchedFile) {
        try {
          const url = await getDownloadURL(matchedFile.ref);
          urls.push(url);
        } catch (error: any) {
          // Silently continue if file fails to load
        }
      }
    } catch (error: any) {
      // Silently continue if listAll fails
    }
  }
  
  // If still no files found and we have fileName, try direct path
  if (urls.length === 0 && fileName && userId) {
    try {
      const fileRef = ref(storage, `invoices/${userId}/${fileName}`);
      const url = await getDownloadURL(fileRef);
      urls.push(url);
    } catch (error: any) {
      // Silently continue if file fails to load
    }
  }
  
  return urls;
}

export async function fetchExistingValidation(searchId: string) {
  const validationDocRef = doc(db, 'invoiceValidations', searchId);
  const validationDoc = await getDoc(validationDocRef);
  if (!validationDoc.exists()) return null;
  return validationDoc.data() as any;
}

export async function runInvoicePrecheck(functionUrl: string, payload: any) {
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    mode: 'cors',
    credentials: 'omit',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
}

